import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ShieldOff } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  PasswordInput,
  Textarea,
} from '@mlm/ui';
import {
  cancelMyArchiveRequest,
  downloadMyDataExport,
  getMyArchiveRequest,
  getTenantConfig,
  requestMyArchive,
} from '../../lib/api';

/** The status code an axios error carries, without importing axios here. */
function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

function errorCodeOf(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
}

/**
 * Privacy & data — the two member-facing doors of Phase 8: take a copy of your
 * data, or ask for the account to be closed.
 *
 * Both mutations translate the server's status/error code into a sentence and
 * throw it, rather than declaring `meta.errorMessage`: the messages differ per
 * failure (rate limit vs wrong password vs staff account) and a single fixed
 * string would have to be vague enough to be useless. The shared MutationCache
 * toasts whatever message the throw carries.
 */
export function PrivacyTab() {
  const qc = useQueryClient();

  const exportMut = useMutation({
    meta: { successMessage: 'Your data file has been downloaded.' },
    mutationFn: async () => {
      try {
        await downloadMyDataExport();
      } catch (err) {
        // The response is fetched as a blob, so axios hands back a Blob body
        // instead of the API's JSON `{error}` — the status is all we have.
        if (statusOf(err) === 429) {
          throw new Error('You can download your data 3 times an hour. Please try again later.');
        }
        throw new Error("Couldn't prepare your data file. Please try again.");
      }
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Download className="h-4 w-4 text-muted-foreground" /> Download my data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Get everything this account holds — your profile, orders, slots, wallet history, course
            enrollments and certificates — as one JSON file. Nothing is deleted or changed by
            downloading it.
          </p>
          <Button size="sm" onClick={() => exportMut.mutate()} disabled={exportMut.isPending}>
            {exportMut.isPending ? 'Preparing…' : 'Download my data'}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Limited to 3 downloads an hour. Large accounts can take a few seconds.
          </p>
        </CardContent>
      </Card>

      <ArchiveCard onChanged={() => qc.invalidateQueries({ queryKey: ['archive-request'] })} />
    </div>
  );
}

/**
 * Account archival. The member REQUESTS and an admin executes — never a
 * self-serve timer — because a member here holds a wallet, sits in a genealogy
 * tree other people are paid through, and may have a cash-out in flight. The
 * copy has to say that plainly, and has to be honest that the company keeps
 * the records it is legally its own: an archived account's purchases and tree
 * position do not disappear.
 */
function ArchiveCard({ onChanged }: { onChanged: () => void }) {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({ queryKey: ['archive-request'], queryFn: getMyArchiveRequest });
  // Same query key the rest of the app uses, so this shares the one cached
  // config fetch. Defaults to the REVERSIBLE reading while it loads: promising
  // erasure we do not perform is the worse of the two wrong answers.
  const cfg = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig });
  const erasing = cfg.data?.accountArchiveMode === 'erase';

  const request = useMutation({
    meta: { successMessage: 'Request sent. An admin will review it.' },
    mutationFn: async () => {
      try {
        return await requestMyArchive({ password, reason: reason.trim() || undefined });
      } catch (err) {
        const code = errorCodeOf(err);
        throw new Error(
          code === 'invalid_password'
            ? 'That password is incorrect.'
            : code === 'archive_already_requested'
              ? 'You already have a request waiting for review.'
              : code === 'staff_cannot_archive'
                ? 'Staff accounts cannot be archived — ask another administrator.'
                : "Couldn't file your request. Please try again.",
        );
      }
    },
    onSuccess: () => {
      setPassword('');
      setReason('');
      setUnderstood(false);
      setError(null);
      onChanged();
    },
    // The message is shown inline as well as toasted: the failure people
    // actually hit is a mistyped password, and that belongs next to the field.
    onError: (err: unknown) => setError((err as Error).message),
  });

  const cancel = useMutation({
    meta: { successMessage: 'Request cancelled.' },
    mutationFn: cancelMyArchiveRequest,
    onSuccess: onChanged,
  });

  const pending = q.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldOff className="h-4 w-4 text-muted-foreground" />{' '}
          {erasing ? 'Request account archival' : 'Close my account'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Two genuinely different operations, so two sets of words. Telling a
            member their data will be erased when the tenant only deactivates
            is as wrong as the reverse, and the reverse is unrecoverable. */}
        <div className="space-y-2 text-sm text-muted-foreground">
          {erasing ? (
            <>
              <p>
                Archiving closes this account for good. You will not be able to log in again, and
                the account cannot be reopened.
              </p>
              <p>
                <span className="font-medium text-foreground">What is erased:</span> your name,
                email address, phone number, mailing address, any ID documents you uploaded, and
                your saved payout details.
              </p>
            </>
          ) : (
            <>
              <p>
                Closing your account signs you out everywhere and stops you signing in. Nothing is
                deleted.
              </p>
              <p>
                <span className="font-medium text-foreground">Changed your mind?</span> Sign in with
                the same email and password and choose Reactivate. Your profile comes back exactly
                as you left it.
              </p>
            </>
          )}
          <p>
            <span className="font-medium text-foreground">What is kept:</span> your purchases,
            course enrollments, certificates and your position in the genealogy tree. Those are also
            the company&apos;s records — orders and commissions paid through your position belong to
            other people&apos;s accounts too, so they stay.
          </p>
          <p>
            An administrator reviews every request before anything happens. This is not instant, and
            you can cancel while it is still waiting.
          </p>
        </div>

        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : pending ? (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm">
              <span className="font-medium">Request pending review.</span> Submitted{' '}
              {new Date(pending.requestedAt).toLocaleDateString()}.
            </p>
            {pending.reason && (
              <p className="text-xs text-muted-foreground">
                Your reason: &ldquo;{pending.reason}&rdquo;
              </p>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
            >
              {cancel.isPending ? 'Cancelling…' : 'Cancel request'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground" htmlFor="archive-reason">
                Why are you leaving? (optional)
              </label>
              <Textarea
                id="archive-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={1000}
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground" htmlFor="archive-password">
                Confirm your password
              </label>
              <PasswordInput
                id="archive-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-1"
              />
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={understood}
                onCheckedChange={(v) => setUnderstood(v === true)}
                className="mt-0.5"
              />
              <span>
                {erasing
                  ? 'I understand this closes my account permanently.'
                  : 'I understand this closes my account and signs me out everywhere.'}
              </span>
            </label>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => request.mutate()}
              disabled={!password || !understood || request.isPending}
            >
              {request.isPending ? 'Sending…' : erasing ? 'Request archival' : 'Request closure'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
